import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { kitchenApi } from '../services/api';
import { useKDSStore, Ticket, TicketItem } from '../stores/kdsStore';
import {
  Clock,
  Users,
  AlertTriangle,
  Crown,
  Check,
  PlayCircle,
  MessageSquare,
} from 'lucide-react';
import clsx from 'clsx';
import { formatDistanceToNowStrict } from 'date-fns';

interface TicketCardProps {
  ticket: Ticket;
  currentTime: Date;
}

export default function TicketCard({ ticket, currentTime }: TicketCardProps) {
  const queryClient = useQueryClient();
  const { updateItem, updateTicket, settings } = useKDSStore();

  // Calculate elapsed time from currentTime prop (updated by parent)
  const elapsedTime = useMemo(() => {
    return formatDistanceToNowStrict(new Date(ticket.sentAt), { addSuffix: false });
  }, [ticket.sentAt, currentTime]);

  // Calculate urgency based on time
  const getUrgency = () => {
    const sentAt = new Date(ticket.sentAt);
    const ageMinutes = Math.floor((Date.now() - sentAt.getTime()) / 60000);

    if (ageMinutes >= settings.criticalThresholdMinutes) return 'critical';
    if (ageMinutes >= settings.warningThresholdMinutes) return 'warning';
    if (ticket.status === 'in_progress') return 'in-progress';
    return 'new';
  };

  const urgency = getUrgency();

  // Start item mutation
  const startItemMutation = useMutation({
    mutationFn: ({ itemId }: { itemId: string }) =>
      kitchenApi.startItem(itemId),
    onSuccess: (_, { itemId }) => {
      updateItem(ticket.id, itemId, {
        status: 'in_progress',
        startedAt: new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
    },
  });

  // Complete item mutation
  const completeItemMutation = useMutation({
    mutationFn: ({ itemId }: { itemId: string }) =>
      kitchenApi.completeItem(itemId),
    onSuccess: (_, { itemId }) => {
      updateItem(ticket.id, itemId, {
        status: 'completed',
        completedAt: new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
    },
  });

  // Bump ticket mutation
  const bumpMutation = useMutation({
    mutationFn: () => kitchenApi.bumpTicket(ticket.id),
    onSuccess: () => {
      updateTicket(ticket.id, { status: 'completed' });
      queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
    },
  });

  const handleItemClick = (item: TicketItem) => {
    if (item.status === 'pending') {
      startItemMutation.mutate({ itemId: item.id });
    } else if (item.status === 'in_progress') {
      completeItemMutation.mutate({ itemId: item.id });
    }
  };

  const handleBump = () => {
    bumpMutation.mutate();
  };

  const allItemsCompleted = ticket.items.every(
    (item) => item.status === 'completed'
  );

  const visibleItems = settings.showCompletedItems
    ? ticket.items
    : ticket.items.filter((item) => item.status !== 'completed');

  const completedCount = ticket.items.filter(
    (item) => item.status === 'completed'
  ).length;

  // Check for any allergen alerts in the ticket
  const allergenAlerts = useMemo(() => {
    const alerts: string[] = [];
    if (ticket.allergenAlert) alerts.push(ticket.allergenAlert);
    ticket.items.forEach(item => {
      if (item.allergenAlert && !alerts.includes(item.allergenAlert)) {
        alerts.push(item.allergenAlert);
      }
    });
    return alerts;
  }, [ticket]);

  const hasAllergenAlert = allergenAlerts.length > 0 || ticket.hasAllergenAlert;

  return (
    <div className={clsx('ticket-card animate-slide-in', urgency, hasAllergenAlert && 'ring-2 ring-red-500')}>
      {/* ALLERGEN ALERT BANNER */}
      {hasAllergenAlert && (
        <div className="bg-red-600 text-white px-3 py-2 flex items-center gap-2 animate-pulse">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="font-bold text-sm uppercase tracking-wide">
            ⚠️ ALLERGY ALERT: {allergenAlerts.join(' | ') || 'CHECK NOTES'}
          </span>
        </div>
      )}

      {/* Header - larger touch targets for iOS */}
      <div className="p-3 sm:p-4 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Course Badge - Prominent display */}
          {ticket.courseNumber && ticket.courseNumber > 0 && (
            <div className="bg-blue-600 text-white px-2.5 py-1 rounded-full font-bold text-sm flex items-center gap-1">
              <span className="text-blue-200 text-xs">C</span>
              <span>{ticket.courseNumber}</span>
            </div>
          )}
          <span className="font-bold text-lg sm:text-xl">
            {ticket.tableName || `Table ${ticket.tableNumber}`}
          </span>
          {ticket.priority === 'vip' && (
            <Crown className="w-5 h-5 text-amber-400" />
          )}
          {ticket.priority === 'rush' && (
            <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-3 text-sm sm:text-base">
          <span className="flex items-center gap-1.5 text-slate-400">
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            {ticket.partySize}
          </span>
          <span
            className={clsx('flex items-center gap-1.5 font-mono', {
              'timer-normal': urgency === 'new' || urgency === 'in-progress',
              'timer-warning': urgency === 'warning',
              'timer-critical': urgency === 'critical',
            })}
          >
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            {elapsedTime}
          </span>
        </div>
      </div>

      {/* Server info + Status Summary */}
      <div className="px-3 sm:px-4 py-2 bg-slate-900/50 text-xs sm:text-sm text-slate-400 flex items-center justify-between">
        <span className="font-medium">{ticket.serverName}</span>
        {/* Status summary badges */}
        <div className="flex items-center gap-1.5">
          {ticket.items.filter(i => i.status === 'pending').length > 0 && (
            <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold">
              {ticket.items.filter(i => i.status === 'pending').length} FIRE
            </span>
          )}
          {ticket.items.filter(i => i.status === 'in_progress').length > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500 text-white rounded text-[10px] font-bold">
              {ticket.items.filter(i => i.status === 'in_progress').length} COOK
            </span>
          )}
          {completedCount > 0 && (
            <span className="px-1.5 py-0.5 bg-emerald-600 text-white rounded text-[10px] font-bold">
              {completedCount} DONE
            </span>
          )}
        </div>
      </div>

      {/* Items - optimized for touch */}
      <div className="divide-y divide-slate-700/30">
        {visibleItems.map((item) => (
          <button
            key={item.id}
            onClick={() => handleItemClick(item)}
            disabled={item.status === 'completed'}
            className={clsx(
              'ticket-item w-full text-left flex items-start gap-3 transition-all btn-press',
              item.status === 'pending' && 'active:bg-slate-700/50',
              item.status === 'in_progress' && 'bg-blue-900/30 active:bg-blue-900/50',
              item.status === 'completed' && 'completed'
            )}
          >
            {/* Status indicator - larger for touch with clear labels */}
            <div className="mt-0.5 flex-shrink-0 flex flex-col items-center">
              {item.status === 'pending' && (
                <>
                  <div className="w-6 h-6 rounded-full border-2 border-red-500 bg-red-500/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-red-400">!</span>
                  </div>
                  <span className="text-[9px] text-red-400 font-bold mt-0.5">FIRE</span>
                </>
              )}
              {item.status === 'in_progress' && (
                <>
                  <PlayCircle className="w-6 h-6 text-amber-400 animate-pulse" />
                  <span className="text-[9px] text-amber-400 font-bold mt-0.5">COOK</span>
                </>
              )}
              {item.status === 'completed' && (
                <>
                  <Check className="w-6 h-6 text-emerald-500" />
                  <span className="text-[9px] text-emerald-400 font-bold mt-0.5">DONE</span>
                </>
              )}
            </div>

            {/* Item details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xl sm:text-2xl text-white">{item.quantity}</span>
                <span className="font-semibold text-base sm:text-lg truncate">{item.name}</span>
              </div>

              {item.modifiers.length > 0 && (
                <p className="text-sm sm:text-base text-amber-400 mt-1 font-medium">
                  {item.modifiers.join(', ')}
                </p>
              )}

              {item.allergenAlert && (
                <p className="text-sm sm:text-base bg-red-600 text-white px-2 py-1 rounded mt-1 flex items-center gap-1.5 font-bold">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>⚠️ {item.allergenAlert}</span>
                </p>
              )}

              {item.specialInstructions && (
                <p className="text-sm sm:text-base text-red-400 mt-1 flex items-start gap-1.5 font-medium">
                  <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{item.specialInstructions}</span>
                </p>
              )}

              {item.seatNumber && (
                <span className="text-xs sm:text-sm text-slate-500 mt-1 block">
                  Seat {item.seatNumber}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Notes */}
      {ticket.notes && (
        <div className="px-3 sm:px-4 py-3 bg-red-900/30 text-sm sm:text-base text-red-300 flex items-start gap-2">
          <MessageSquare className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="font-medium">{ticket.notes}</span>
        </div>
      )}

      {/* Bump button - large touch target */}
      <button
        onClick={handleBump}
        disabled={!allItemsCompleted || bumpMutation.isPending}
        className={clsx(
          'bump-btn',
          allItemsCompleted && !bumpMutation.isPending && 'bg-emerald-600 active:bg-emerald-700'
        )}
      >
        {bumpMutation.isPending ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Bumping...
          </span>
        ) : allItemsCompleted ? (
          'BUMP'
        ) : (
          `${ticket.items.length - completedCount} items remaining`
        )}
      </button>
    </div>
  );
}
